import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  selectMeteorologyFieldsModelPerformancePeriodType,
  selectMeteorologyFieldsModelPerformanceAnalysisType,
  selectMeteorologyFieldsModelPerformanceVariable,
  selectMeteorologyFieldsModelPerformancePentad,
  selectMeteorologyFieldsModelPerformanceRounds,
  selectMeteorologyFieldsModelPerformanceSelectedRounds,
  selectMeteorologyFieldsModelPerformanceAnalysisPeriod,
  selectMeteorologyFieldsModelPerformanceMapLoading,
  selectMeteorologyFieldsModelPerformanceMapError,
  setFilter,
} from 'redux/reducers/meteorologyFieldsModelPerformanceReducer';
import {
  modelPerformanceApi,
  useLazyGetModelPerformanceMapDataQuery,
} from 'services/api/Meteorology/Fields/ModelPerformance';
import { store } from 'redux/store';
import { GetModelPerformanceMapDataParams } from 'types/Meteorology/Fields/ModelPerformance/getMapData';
import { getMockMapData } from 'mock/api/Meteorology/Fields/ModelPerformance';
import { ResponseError } from 'services/api/base';

const USE_MOCK_MAP_DATA = false;

// Só a consulta mais recente escreve o mapa: na troca rápida entre pêntadas, a resposta
// atrasada de uma consulta anterior chegaria por cima. Compartilhado entre as instâncias
// do hook (painel, mobile e usePentadRoundsSync disparam consultas).
let ultimaConsulta = 0;

export default function useMapData() {
  const periodType = useSelector(selectMeteorologyFieldsModelPerformancePeriodType);
  const analysisType = useSelector(selectMeteorologyFieldsModelPerformanceAnalysisType);
  const variable = useSelector(selectMeteorologyFieldsModelPerformanceVariable);
  const pentad = useSelector(selectMeteorologyFieldsModelPerformancePentad);
  const rounds = useSelector(selectMeteorologyFieldsModelPerformanceRounds);
  const selectedRounds = useSelector(selectMeteorologyFieldsModelPerformanceSelectedRounds);
  const mapLoading = useSelector(selectMeteorologyFieldsModelPerformanceMapLoading);
  const mapError = useSelector(selectMeteorologyFieldsModelPerformanceMapError);

  const [mockLoading, setMockLoading] = useState(false);

  const [triggerQuery] = useLazyGetModelPerformanceMapDataQuery();

  const isLoading = USE_MOCK_MAP_DATA ? mockLoading : mapLoading;

  const params = useMemo<GetModelPerformanceMapDataParams | null>(
    () => {
      if (!periodType || !analysisType || !variable) {
        return null;
      }

      if (periodType === 'preset') {
        if (!pentad || !rounds || rounds.length === 0) {
          return null;
        }
        return {
          tipo_analise: analysisType,
          variavel: variable,
          periodo: pentad,
          datas_rodada: rounds,
        };
      }

      if (periodType === 'free') {
        const hasRounds = selectedRounds && selectedRounds.length > 0;
        if (!hasRounds) {
          return null;
        }
        return {
          tipo_analise: analysisType,
          variavel: variable,
          periodo: 'livre',
          datas_rodada: selectedRounds,
        };
      }

      return null;
    },
    [
      periodType,
      analysisType,
      variable,
      pentad,
      rounds,
      selectedRounds,
    ],
  );

  const triggerMapDataRequest = useCallback(
    // `preferCache`: a troca de pêntada reexibe do cache uma consulta já feita; o botão
    // sempre pede dado novo. Comparado com `true` porque o onClick do mobile passa o evento.
    async (options?: { preferCache?: boolean }) => {
      if (!params) return;
      const preferCache = options?.preferCache === true;
      ultimaConsulta += 1;
      const consulta = ultimaConsulta;

      store.dispatch(setFilter({ mapData: undefined, mapError: undefined, mapLoading: true }));

      if (USE_MOCK_MAP_DATA) {
        setMockLoading(true);
        try {
          await new Promise((resolve) => { setTimeout(resolve, 500); });
          const analysisTypeKey = params.tipo_analise === 'regioes' ? 'region' : 'basin';
          store.dispatch(setFilter({
            mapData: getMockMapData(analysisTypeKey),
            mapLoading: false,
          }));
        } finally {
          setMockLoading(false);
        }
        return;
      }

      let mapParams: GetModelPerformanceMapDataParams = params;

      if (params.periodo === 'livre') {
        const validityResult = await store.dispatch(
          modelPerformanceApi.endpoints.listarDatasValidadePrevisao.initiate({
            tipo_analise: params.tipo_analise,
            variavel: params.variavel,
            datas_rodada: params.datas_rodada,
          }),
        );
        if ('error' in validityResult || !validityResult.data?.length) {
          const err = 'error' in validityResult
            ? (validityResult.error as ResponseError)
            : null;
          store.dispatch(setFilter({
            mapData: undefined,
            mapError: {
              title: err?.data ?? 'Erro',
              message: err?.description ?? 'Não foi possível obter as datas de validade.',
            },
            mapLoading: false,
          }));
          return;
        }
        const currentPeriod = selectMeteorologyFieldsModelPerformanceAnalysisPeriod(
          store.getState(),
        );
        let filteredDates = validityResult.data;
        if (currentPeriod?.start && currentPeriod?.end) {
          filteredDates = validityResult.data.filter(
            (d) => d >= currentPeriod.start && d <= currentPeriod.end,
          );
        }
        mapParams = { ...params, datas_validade: filteredDates };
      }

      const response = await triggerQuery(mapParams, preferCache);
      if (consulta !== ultimaConsulta) return;

      if ('data' in response && response.data) {
        store.dispatch(setFilter({
          mapData: response.data,
          mapError: undefined,
          mapLoading: false,
        }));
      } else if ('error' in response) {
        const err = response.error as ResponseError;
        store.dispatch(setFilter({
          mapData: undefined,
          mapError: { title: err.data, message: err.description || '' },
          mapLoading: false,
        }));
      } else {
        store.dispatch(setFilter({ mapLoading: false }));
      }
    },
    [params, triggerQuery],
  );

  return {
    triggerMapDataRequest,
    isLoading,
    error: mapError,
    params,
  };
}
